<template>
  <div class="bg-white rounded-3xl p-4 shadow-md relative overflow-hidden category-container">
    <template v-if="!loading && categoryDetails">
      <div class="grid grid-cols-6 w-full items-self-start gap-10">
        <div class="col-span-2 category-img-container rounded-2xl overflow-hidden relative">
          <img
            class="w-full h-full"
            :src="categoryDetails?.imageURI"
            :alt="categoryDetails?.name"
            :class="{ 'gray-scale': !categoryDetails?.enabled }" />

          <div
            class="absolute text-xs bg-gray-600 text-white w-full text-center rotate-45"
            :class="{'none': categoryDetails?.enabled}">Disabled</div>
        </div>
        <div class="col-span-4 flex flex-col gap-5">
          <h2>{{ categoryDetails?.name }}</h2>
          <h3 v-if="categoryDetails?.description" class="text-gray-600 font-light text-md">{{ categoryDetails?.description }}</h3>

          <div class="flex gap-4">
            <button
              title="Delete"
              class="text-red-600 hover:text-white bg-red-50 hover:bg-red-600 rounded-full transition-colors flex items-center justify-center w-9 h-9 shadow-md border-none cursor-pointer"
              @click.stop="confirmAction">
              <i aria-hidden="true" class="fa fa-trash"></i>
            </button>
            <button
              :title="categoryDetails?.enabled ? 'Disable' : 'Enable'"
              class="text-blue-600 hover:text-white bg-blue-50 hover:bg-blue-600 rounded-full transition-colors flex items-center justify-center w-9 h-9 shadow-md border-none cursor-pointer"
              @click.stop="onToggleClicked(categoryDetails)">
              <i :class="{'fa fa-low-vision': categoryDetails?.enabled, 'fas fa-eye': !categoryDetails?.enabled}"></i>
            </button>
          </div>
        </div>

        <div class="col-span-6">
          <TabsOptions
            :options="tabs"
            :selectedOption="selectedOption"
            @update:selectedOption="updateSelectedOption"
          />
        </div>

        <div class="col-span-6">
          <CustomFields
            v-if="selectedOption === 'custom_fields'"
            :items="categoryCustomFields"
            @onRefresh="getCategoryDetails"/>
          <TimeEvents v-else-if="selectedOption === 'time_events'"/>
          <ChangeLogs v-else-if="selectedOption === 'change_logs'"/>
          <UpdateCategory
            v-else-if="selectedOption === 'update_category'"
            :categoryDetails="{
              posId: categoryDetails?.posId,
              displayOrder: categoryDetails?.displayOrder,
              url: categoryDetails?.imageURI,
              type: categoryDetails?.imageURI ? 'url' : 'image',
              enabled: !!categoryDetails?.enabled,
              name: {
                [primaryLang()]: categoryDetails?.[`name_${primaryLang(true)}`],
                [secondaryLang()]: categoryDetails?.[`name_${secondaryLang(true)}`],
              },
              description: {
                [primaryLang()]: categoryDetails?.[`description_${primaryLang(true)}`],
                [secondaryLang()]: categoryDetails?.[`description_${secondaryLang(true)}`],
              }
            }"
            @getCategoryDetails="getCategoryDetails"/>
        </div>
      </div>

      <BaseModal
        v-if="showConfirmModal"
        :title="confirmationConfig.title"
        @close="cancelAction"
        headerClass="py-3"
      >
        <template v-slot:body>
          <div class="flex items-center py-3">
            <div
              class="mr-2 flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full"
              :class="confirmationConfig.iconBgClass"
            >
              <i :class="confirmationConfig.iconClass"></i>
            </div>
            <div class="text-center sm:text-left">
              <p class="text-lg mb-0 font-medium">
                {{ confirmationConfig.message }}
              </p>
            </div>
          </div>
        </template>
        <template v-slot:footer>
          <div class="w-full flex justify-end py-3">
            <button
              :id="confirmationConfig.cancelButtonId"
              @click="cancelAction"
              class="rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              {{ translate("Cancel") }}
            </button>
            <button
              :id="confirmationConfig.confirmButtonId"
              @click="executeAction"
              class="rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
              :class="confirmationConfig.confirmButtonClass"
            >
              {{ confirmationConfig.confirmButtonText }}
            </button>
          </div>
        </template>
      </BaseModal>
    </template>
    <template v-else-if="loading">
      <BaseLoader
        showText
        :loadingText="'Loading Category...'"
        size="xl"/>
    </template>
    <template v-else>
      No Category Details (Invalid ID: {{ categoryId }})
    </template>
  </div>
</template>

<script>
import { notificationAlerts, translations } from '@/mixins';
import TabsOptions from '@/components/V2/Tabs/TabsOptions.vue';
import ChangeLogs from './components/ChangeLogs.vue';
import CustomFields from './components/CustomFields.vue';
import TimeEvents from './components/TimeEvents.vue';
import UpdateCategory from './components/UpdateCategory.vue';
import { deleteCategory, enableDisableSpecificCategory, getCategoryDetails } from '@/services/V2/menus/categories.service';
import BaseLoader from '@/components/BaseLoader.vue';
import BaseModal from '@/components/BaseModal.vue';

export default {
  name: 'CategoryDetails',

  mixins: [notificationAlerts, translations],

  components: {
    TabsOptions,
    BaseLoader,
    CustomFields,
    ChangeLogs,
    TimeEvents,
    UpdateCategory,
    BaseModal,
  },

  computed: {
    categoryCustomFields () {
      return this.categoryDetails?.customFields?.map((item) => {
        return {
          value: item?.value,
          id: item?.['custom-field-id'],
          field: item?.['custom-field'],
        };
      });
    },
    confirmationConfig () {
      const configs = {
        delete: {
          title: this.translate('Delete Category'),
          message: this.translate('Are you sure you want to delete this catgeory?'),
          iconClass: 'fas fa-trash-alt text-red-600',
          iconBgClass: 'bg-red-100',
          confirmButtonText: this.translate('Delete'),
          confirmButtonClass: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
          confirmButtonId: 'menus.deletemenu',
          cancelButtonId: 'menus.closedeletemenumodal',
        },
      };
      return configs[this.currentAction] || configs.delete;
    },
  },

  data () {
    return {
      loading: true,
      menuId: null,
      categoryId: null,
      categoryDetails: null,
      showConfirmModal: false,
      selectedOption: this.$route.query.tab || 'update_category',
      tabs: [
        {
          value: 'items',
          label: 'Items',
          disabled: true,
        },
        {
          value: 'custom_fields',
          label: 'Custom Fields',
          disabled: true,
        },
        {
          value: 'time_events',
          label: 'Time Events',
          disabled: true,
        },
        {
          value: 'change_logs',
          label: 'Change Logs',
        },
        {
          value: 'update_category',
          label: 'Update Category',
        },
      ],
    };
  },

  watch: {
    '$route.query.tab' (tab) {
      this.selectedOption = tab;
    },
  },

  mounted () {
    this.menuId = this.$route?.params['menuId'];
    this.categoryId = this.$route?.params['categoryId'];
    this.getCategoryDetails();
  },

  methods: {
    async getCategoryDetails () {
      this.loading = true;
      try {
        const { data: responseDetails } = await getCategoryDetails(this.menuId, this.categoryId, true);
        this.categoryDetails = {
          id: responseDetails?.id,
          name: responseDetails?.name_en,
          description: responseDetails?.description_en,
          [`name_${this.primaryLang(true)}`]: responseDetails?.[`name_${this.primaryLang(true)}`],
          [`name_${this.secondaryLang(true)}`]: responseDetails?.[`name_${this.secondaryLang(true)}`],
          [`description_${this.primaryLang(true)}`]: responseDetails?.[`description_${this.primaryLang(true)}`],
          [`description_${this.secondaryLang(true)}`]: responseDetails?.[`description_${this.secondaryLang(true)}`],
          imageURI: responseDetails?.image_uri,
          enabled: responseDetails?.enabled,
          customFields: responseDetails?.attributes?.['custom-fields'] || [],
          posId: responseDetails?.code,
          displayOrder: responseDetails?.display_order,
        };
      } catch (error) {
        console.error('ERROR!', 'Error fetching categories!');
      } finally {
        this.loading = false;
      }
    },

    async onToggleClicked (category) {
      try {
        await enableDisableSpecificCategory(this.menuId, category?.id, !category?.enabled);
        this.successNotification('Updated!', 'Category Updated Successfully');
        this.getCategoryDetails();
      } catch (e) {
        this.systemErrorNotification('ERROR!', 'Something went wrong!');
      }
    },

    async executeAction () {
      if (!this.categoryId) return;

      try {
        await deleteCategory(this.menuId, this.categoryId);
        this.successNotification(
          'CATEGORY DELETED!',
          'Category has been deleted successfully',
        );
        this.$router.push({ name: 'categories list', params: { menuId: this.menuId } });
      } catch (error) {
        this.systemErrorNotification('SYSTEM ERROR!', 'Something went wrong, please try again!');
      } finally {
        this.showConfirmModal = false;
        this.currentAction = null;
        this.categoryId = null;
      }
    },

    confirmAction () {
      this.showConfirmModal = true;
    },

    cancelAction () {
      this.showConfirmModal = false;
    },

    updateSelectedOption (tab) {
      this.selectedOption = tab;
      this.$router.replace({ query: { ...this.$route.query, tab } });
    },

    primaryLang (initialOnly = false) {
      return !initialOnly ? this.getConceptSettings?.attributes?.['primary-language'] : this.getConceptSettings?.attributes?.['primary-language']?.split('-')[0];
    },
    secondaryLang (initialOnly = false) {
      return !initialOnly ? this.getConceptSettings?.attributes?.['secondary-language'] : this.getConceptSettings?.attributes?.['secondary-language']?.split('-')[0];
    },
  },
};
</script>

<style scoped>
  .category-container {
    min-height: 80vh;
  }
  .category-img-container {
    border: 1px solid #DDD;
    max-height: 30rem;
  }
  .rotate-45 {
    transform: rotate(45deg);
    right: -40%;
    top: 10%;
  }
  .none {
    display: none;
  }
  .gray-scale {
    filter: grayscale(100%);
  }
</style>
